/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { MouseEventHandler } from "react";
import { forwardRef, useContext } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  constantSemanticColors,
  numericPercentages,
  numericPixels,
  spacing,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { Text } from "../../_core/Text/Text.emotion";
import type { IconElement } from "../../icons/Icon/Icon";
import { ChevronRightIcon } from "../../icons/Icon/NavigationIcons";
import { ModalTabContext } from "../ModalTabContainer/ModalTabContainer.emotion";

const Element: HallowElement = "div";

const baseStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  ":hover": { backgroundColor: theme.semanticColors.onOverlayLow },
  ":active": { backgroundColor: theme.semanticColors.onOverlayLow },
  borderRadius: spacing.m,
  cursor: "pointer",
  display: "flex",
  gap: spacing.m,
  minWidth: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: { minWidth: numericPixels[300] },
  padding: spacing.m,
  width: numericPercentages[100],
});
const disabledStyle: Interpolation = {
  backgroundColor: colors.transparent,
  cursor: "default",
  opacity: 0.5,
};
const divStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.t,
};
const labelStyle: Interpolation = {
  color: constantSemanticColors.onOverlayDarkMed,
  textTransform: "uppercase",
};

export type ModalTabProps = WithAsChild<HallowElementProps<typeof Element>> & {
  label?: string;
  showChevron?: boolean;
  startIcon?: IconElement;
  value: string;
  disabled?: boolean;
};

export const ModalTab = forwardRef<HTMLDivElement, ModalTabProps>(
  (
    {
      asChild = false,
      children,
      label,
      showChevron = true,
      startIcon,
      value,
      onClick,
      disabled,
      ...props
    },
    ref,
  ) => {
    const tabs = useContext(ModalTabContext);

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement: Element,
    });

    const changeTabs: MouseEventHandler<HTMLDivElement> = (e) => {
      if (disabled) return;
      tabs.setValue(value);
      if (onClick) onClick(e);
    };

    return (
      <DeterminedElement
        onClick={changeTabs}
        css={[baseStyle, disabled && disabledStyle]}
        aria-disabled={disabled}
        data-tab={value}
        {...props}
        ref={ref}
      >
        {startIcon && startIcon}
        <div css={divStyle}>
          {label && (
            <Text size="l" type="detail" css={labelStyle}>
              {label}
            </Text>
          )}
          <Text size="m" type="title">
            {children}
          </Text>
        </div>
        {showChevron && !disabled && <ChevronRightIcon />}
      </DeterminedElement>
    );
  },
);

ModalTab.displayName = "ModalTab";
